/**
 * DocumentoPublico.js
 * Componente público para ver/descargar documentos compartidos.
 * Accesible sin autenticación via /d/:token
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL, FUNCTIONS_URL } from '../services/api/config';

export default function DocumentoPublico() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [docInfo, setDocInfo] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Token no proporcionado');
      setLoading(false);
      return;
    }

    fetchDocumentInfo();
  }, [token]);

  const fetchDocumentInfo = async () => {
    try {
      // Obtener info del documento desde el API
      const response = await fetch(`${API_URL}/api/documentos-publicos/${token}/info`);

      if (!response.ok) {
        if (response.status === 404) {
          setError('Documento no encontrado o enlace expirado');
        } else {
          setError('Error al cargar el documento');
        }
        setLoading(false);
        return;
      }

      const info = await response.json();
      setDocInfo(info);

      // URL directa a Cloud Functions para ver/descargar el documento
      // Usamos FUNCTIONS_URL porque el hosting bloquea iframes
      setDocumentUrl(`${FUNCTIONS_URL}/api/documentos-publicos/${token}`);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching document:', err);
      setError('Error de conexión');
      setLoading(false);
    }
  };

  const handleDownload = () => {
    // Abrir en nueva pestaña para descargar
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = docInfo?.nombre || 'documento';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isImage = docInfo?.tipo?.startsWith('image/');
  const isPDF = docInfo?.tipo === 'application/pdf';

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted">Cargando documento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <i className="bi bi-exclamation-circle text-danger" style={{ fontSize: '4rem' }}></i>
          <h4 className="mt-3">No se pudo cargar el documento</h4>
          <p className="text-muted">{error}</p>
          <a href="/" className="btn btn-primary mt-3">
            <i className="bi bi-house me-2"></i>Ir al inicio
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-white border-bottom py-3 px-4 sticky-top">
        <div className="container-fluid">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className={`bi ${isPDF ? 'bi-file-earmark-pdf-fill text-danger' : 'bi-file-earmark-image-fill text-primary'} fs-4 me-2`}></i>
              <div>
                <h5 className="mb-0">{docInfo?.nombre || 'Documento'}</h5>
                <small className="text-muted">
                  {docInfo?.tamañoMB} MB
                  {!docInfo?.puedeDescargar && (
                    <span className="ms-2 badge bg-secondary">Solo lectura</span>
                  )}
                </small>
              </div>
            </div>

            <div className="d-flex gap-2">
              {docInfo?.puedeDescargar && (
                <button className="btn btn-primary" onClick={handleDownload}>
                  <i className="bi bi-download me-1"></i>
                  Descargar
                </button>
              )}
              <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                <i className="bi bi-arrow-left"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer */}
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-center">
          {isImage && (
            <div className="bg-white rounded shadow-sm p-3" style={{ maxWidth: '100%' }}>
              <img
                src={documentUrl}
                alt={docInfo?.nombre}
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 200px)',
                  objectFit: 'contain'
                }}
              />
            </div>
          )}

          {isPDF && (
            <div className="bg-white rounded shadow-sm w-100" style={{ height: 'calc(100vh - 150px)' }}>
              <iframe
                src={documentUrl}
                title={docInfo?.nombre}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px'
                }}
              />
            </div>
          )}

          {!isImage && !isPDF && (
            <div className="text-center py-5">
              <i className="bi bi-file-earmark fs-1 text-muted"></i>
              <p className="mt-3">
                No se puede previsualizar este tipo de archivo.
                {docInfo?.puedeDescargar && (
                  <>
                    <br />
                    <button className="btn btn-primary mt-3" onClick={handleDownload}>
                      <i className="bi bi-download me-1"></i>
                      Descargar archivo
                    </button>
                  </>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer info */}
      {!docInfo?.puedeDescargar && (
        <div className="fixed-bottom bg-warning bg-opacity-25 py-2 text-center">
          <small>
            <i className="bi bi-eye me-1"></i>
            Este documento es de solo lectura. No está permitida la descarga.
          </small>
        </div>
      )}
    </div>
  );
}
